import pluralize from 'pluralize'

import { STATEMENT_STATUSES } from './constants'
import type { ExcludedRange, Statement } from './stores'

export const CUT_BY_TRIAL = 'cut_by_trial'
export const TRIAL_CUT_FAILED = 'TRIAL_CUT_FAILED'

export interface TrialContentsNote {
  label: string
  tone: 'failed' | 'muted'
}

export const isCutByTrial = (range: ExcludedRange) =>
  range.documentClass === CUT_BY_TRIAL

export const pagesSent = (statement: Statement) =>
  statement.originalNumberOfPages ?? statement.numberOfPages

export const pagesRead = (statement: Statement) =>
  Math.min(
    statement.numberOfPages,
    statement.trialPageLimit ?? statement.numberOfPages,
  )

export const pagesKeptUnread = (statement: Statement) =>
  pagesSent(statement) - pagesRead(statement)

export interface TrialReadSummary {
  pagesRead: number
  pagesSent: number
  statementsRead: number
}

const hasStarted = (statement: Statement) =>
  statement.status !== STATEMENT_STATUSES.notStarted

export const summarizeTrialRead = (
  statements: Statement[],
): TrialReadSummary => {
  const started = statements.filter(hasStarted)
  return {
    pagesRead: started.reduce(
      (total, statement) => total + pagesRead(statement),
      0,
    ),
    pagesSent: statements.reduce(
      (total, statement) => total + pagesSent(statement),
      0,
    ),
    statementsRead: started.reduce(
      (total, statement) => total + (statement.segments ?? []).length,
      0,
    ),
  }
}

export const hasFullFileToRead = (statement: Statement) =>
  statement.originalNumberOfPages != null

// Every range of the cut file was thrown out, so the trial read no statement from it.
export const isEmptiedByTrialCut = (statement: Statement) =>
  statement.status === STATEMENT_STATUSES.succeeded &&
  (statement.segments ?? []).length === 0 &&
  (statement.excludedRanges ?? []).some(isCutByTrial)

// A file the budget never reached stays not started. That only means "not read" once the trial has
// no pages left; before then it is simply waiting for the Process button.
const isLeftOutOfTrial = (statement: Statement, trialPagesLeft: number) =>
  statement.status === STATEMENT_STATUSES.notStarted && trialPagesLeft <= 0

export const trialContentsNote = (
  statement: Statement,
  trialPagesLeft: number | null,
): TrialContentsNote | null => {
  if (statement.failureReason === TRIAL_CUT_FAILED) {
    return {
      label: 'Could not cut this PDF. Send a shorter one.',
      tone: 'failed',
    }
  }
  const unread = pagesKeptUnread(statement)
  if (unread > 0) {
    return {
      label: `${unread} ${pluralize('page', unread)} not extracted yet`,
      tone: 'muted',
    }
  }
  if (trialPagesLeft != null && isLeftOutOfTrial(statement, trialPagesLeft)) {
    return { label: 'Not extracted during the trial', tone: 'muted' }
  }
  return null
}

interface ProjectsListState {
  isTrial: boolean
  isLoadingProjects: boolean
  projectCount: number
}

// A trial case is opened only on the welcome page, so a trial that left it without one goes back there.
export const isTrialWithoutCase = ({
  isTrial,
  isLoadingProjects,
  projectCount,
}: ProjectsListState) => isTrial && !isLoadingProjects && projectCount === 0
